/**
 * Frame3_11 - Key Contacts Summary
 *
 * Purpose: Display executor, beneficiary, and important contacts from estate plan
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Users, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/Button';
import { useSettlementSession } from '../../hooks/useSettlementSession';

export default function Frame3_11() {
  const navigate = useNavigate();
  const { estateId } = useParams();
  const { loadPlannerData } = useSettlementSession(estateId || null);
  const [contacts, setContacts] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!estateId) {
        setLoading(false);
        return;
      }

      try {
        const contactsData = await loadPlannerData('frame-146');
        setContacts(contactsData);
      } catch (error) {
        console.error('Failed to load contacts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [estateId, loadPlannerData]);

  const handleNext = () => {
    navigate(estateId ? `/frame-3-2/${estateId}` : '/frame-3-2');
  };

  const handleBack = () => {
    navigate(estateId ? `/frame-3-1/${estateId}` : '/frame-3-1');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading contacts...</div>
      </div>
    );
  }

  const executors = contacts?.contacts?.filter((c: unknown) => c.relationship === 'executor') || [];
  const beneficiaries =
    contacts?.contacts?.filter((c: unknown) => c.relationship === 'beneficiary') || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Key Contacts</h1>
          <p className="text-gray-600">
            Important people designated in the estate plan
          </p>
        </div>

        {/* Executors */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-6 h-6 text-cyan-600" />
            <h2 className="text-xl font-semibold text-gray-900">Executors</h2>
          </div>
          {executors.length > 0 ? (
            <div className="space-y-3">
              {executors.map((executor: unknown, index: number) => (
                <div key={index} className="border border-gray-200 rounded p-4">
                  <div className="font-semibold text-gray-900">
                    {executor.firstName} {executor.lastName}
                  </div>
                  <div className="text-sm text-gray-600">{executor.email}</div>
                  <div className="text-sm text-gray-600">{executor.phone}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic">No executors specified</p>
          )}
        </div>

        {/* Beneficiaries */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-6 h-6 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">Beneficiaries</h2>
          </div>
          {beneficiaries.length > 0 ? (
            <div className="space-y-3">
              {beneficiaries.map((beneficiary: unknown, index: number) => (
                <div key={index} className="border border-gray-200 rounded p-4">
                  <div className="font-semibold text-gray-900">
                    {beneficiary.firstName} {beneficiary.lastName}
                  </div>
                  <div className="text-sm text-gray-600">{beneficiary.email}</div>
                  <div className="text-sm text-gray-600">{beneficiary.phone}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic">No beneficiaries specified</p>
          )}
        </div>

        <div className="flex justify-between items-center">
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button onClick={handleNext}>
            View Assets
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
